import type { FunctionComponent } from 'react';
import { paletteHelper, spacingHelper, Text, typographyHelper } from '@property.com.au/ui-kit';
import styled from 'styled-components';
import { DialogModal } from '../../../../../app-kit/components/DialogModal/DialogModal';
import { OutboundLink } from '../../../../../app-kit/components/OutboundLink/OutboundLink';

const BodyText = styled(Text)`
  ${typographyHelper('body01')};
  color: ${paletteHelper('textSecondary')};
`;

const FineprintText = styled(Text)`
  ${typographyHelper('body02')};
  color: ${paletteHelper('textSecondary')};
`;

const TableHeader = styled.th`
  ${typographyHelper('body02Bold')};
  color: ${paletteHelper('textPrimary')};
  padding: ${spacingHelper('medium')};
  text-align: left;
`;

const TableColumnHeader = styled(TableHeader)`
  text-align: right;
  width: 1%;
  white-space: nowrap;
  padding-left: 0;
`;

const StyledTable = styled.table`
  display: block;
  overflow-x: auto;
  white-space: nowrap;
`;

const TableData = styled.td`
  ${typographyHelper('body01')};
  color: ${paletteHelper('textSecondary')};

  border: 1px solid ${paletteHelper('borderPrimary')};
  padding: ${spacingHelper('medium')};
`;

const StyledOutboundLink = styled(OutboundLink)`
  color: ${paletteHelper('textSecondary')};
  &:hover {
    color: ${paletteHelper('textSecondary')};
  }
`;

const CreativeCommonsLinkV2POINT5: FunctionComponent = () => {
  return (
    <StyledOutboundLink
      href="https://creativecommons.org/licenses/by/2.5/"
      outboundLinkType="creative-commons-outbound-link"
      variant="inline"
      rel="noopener noreferrer"
    >
      CC BY 2.5
    </StyledOutboundLink>
  );
};

export const InternetAvailabilityDisclaimerContent: FunctionComponent = () => (
  <>
    <BodyText>
      With the need for flexible working arrangements, streaming video, gaming and critical internet
      connectivity - having a great connection with a reliable backup option is more important than
      ever.
    </BodyText>
    <BodyText>
      We&apos;ve put together a simple guide to connection types based on their capable speed and
      latency (the time it takes for a small data set to be transmitted from your device to a server
      on the internet and back to your device again).
    </BodyText>
    <StyledTable>
      <tbody>
        <tr>
          <TableHeader>&nbsp;</TableHeader>
          <TableHeader>5G</TableHeader>
          <TableHeader>4G</TableHeader>
          <TableHeader>3G</TableHeader>
        </tr>
        <tr>
          <TableColumnHeader>FTTP</TableColumnHeader>
          <TableData>Amazing</TableData>
          <TableData>Excellent</TableData>
          <TableData>Good</TableData>
        </tr>
        <tr>
          <TableColumnHeader>FTTC</TableColumnHeader>
          <TableData>Excellent</TableData>
          <TableData>Excellent</TableData>
          <TableData>Good</TableData>
        </tr>
        <tr>
          <TableColumnHeader>HFC</TableColumnHeader>
          <TableData>Excellent</TableData>
          <TableData>Excellent</TableData>
          <TableData>Adequate</TableData>
        </tr>
        <tr>
          <TableColumnHeader>FTTN</TableColumnHeader>
          <TableData>Good</TableData>
          <TableData>Good</TableData>
          <TableData>Adequate</TableData>
        </tr>
        <tr>
          <TableColumnHeader>FTTB</TableColumnHeader>
          <TableData>Good</TableData>
          <TableData>Good</TableData>
          <TableData>Limited</TableData>
        </tr>
        <tr>
          <TableColumnHeader>Fixed Wireless</TableColumnHeader>
          <TableData>Good</TableData>
          <TableData>Adequate</TableData>
          <TableData>Limited</TableData>
        </tr>
        <tr>
          <TableColumnHeader>Satellite</TableColumnHeader>
          <TableData>Good</TableData>
          <TableData>Adequate</TableData>
          <TableData>Poor</TableData>
        </tr>
      </tbody>
    </StyledTable>
    <FineprintText>
      This information is collected based on the best available third-party data and should be used
      for reference purposes only. The actual performance of internet connections depends on several
      factors including but not limited to your provider, plan, quality of network and networking
      equipment. Property.com.au makes no representations or warranties regarding the accuracy of
      this data. Always check with your preferred provider to see what options are available at this
      property.
    </FineprintText>
    <FineprintText>
      NBN data © Department of Infrastructure, Transport, Regional Development and Communications.
      Licensed under the Creative Common Attribution Australia (
      <CreativeCommonsLinkV2POINT5 />) License.
    </FineprintText>
    <FineprintText>Mobile Data Source: ACCC © Commonwealth of Australia</FineprintText>
  </>
);

interface InternetAvailabilityDisclaimerProps {
  opened: boolean;
  onClose: () => void;
}

export const InternetAvailabilityDisclaimer: FunctionComponent<
  InternetAvailabilityDisclaimerProps
> = ({ opened, onClose }) => {
  return (
    <DialogModal
      opened={opened}
      onClose={onClose}
      title="Internet connection data & rating"
      primaryButtonText="Got it"
      primaryButtonOnClick={onClose}
      maxHeight="640px"
    >
      <InternetAvailabilityDisclaimerContent />
    </DialogModal>
  );
};
